// TelcoTrack — chain multi-segment route polylines for construction diary map/PDF preview.
// Parity with web `buildRoutePointsFromBatchSegments` (map-route-batch-restore.util.ts).

import { formatAdjacentPointHash } from './adjacent-point-hash.util';

export interface DiaryRouteSegmentInput {
  objectId: string;
  /** WGS84 vertices `[lng, lat]` — at least two for a chainable edge. */
  path: [number, number][];
}

function endpointHash(lng: number, lat: number): string {
  return formatAdjacentPointHash(lng, lat);
}

function appendPath(
  points: [number, number][],
  path: [number, number][],
  skipFirst: boolean,
): void {
  const start = skipFirst ? 1 : 0;
  for (let i = start; i < path.length; i++) {
    const p = path[i]!;
    const prev = points[points.length - 1];
    if (prev && prev[0] === p[0] && prev[1] === p[1]) continue;
    points.push(p);
  }
}

function prependPath(
  points: [number, number][],
  path: [number, number][],
  skipLast: boolean,
): void {
  const limit = skipLast ? path.length - 1 : path.length;
  for (let i = limit - 1; i >= 0; i--) {
    const p = path[i]!;
    const next = points[0];
    if (next && next[0] === p[0] && next[1] === p[1]) continue;
    points.unshift(p);
  }
}

type ChainedSegment = { id: string; path: [number, number][] };

function chainOneFromEnd(
  remaining: Set<string>,
  pathById: ReadonlyMap<string, [number, number][]>,
  endKey: string,
): ChainedSegment | null {
  for (const id of remaining) {
    const path = pathById.get(id);
    if (!path || path.length < 2) continue;
    const startKey = endpointHash(path[0]![0], path[0]![1]);
    const pathEndKey = endpointHash(path[path.length - 1]![0], path[path.length - 1]![1]);
    if (startKey === endKey) return { id, path };
    if (pathEndKey === endKey) return { id, path: [...path].reverse() as [number, number][] };
  }
  return null;
}

function chainOneFromStart(
  remaining: Set<string>,
  pathById: ReadonlyMap<string, [number, number][]>,
  startKey: string,
): ChainedSegment | null {
  for (const id of remaining) {
    const path = pathById.get(id);
    if (!path || path.length < 2) continue;
    const pathStartKey = endpointHash(path[0]![0], path[0]![1]);
    const pathEndKey = endpointHash(path[path.length - 1]![0], path[path.length - 1]![1]);
    if (pathEndKey === startKey) return { id, path };
    if (pathStartKey === startKey) return { id, path: [...path].reverse() as [number, number][] };
  }
  return null;
}

/**
 * Poskládá WGS84 polyline z segmentů batch — řetězí přes sdílené konce od kotvy
 * (vpřed i vzad). Nepřipojuje odpojené segmenty — zabrání chordu první↔poslední.
 */
export function buildChainedRoutePolylineFromSegments(
  segments: readonly DiaryRouteSegmentInput[],
  anchorObjectId?: string | null,
): [number, number][] {
  const byId = new Map<string, [number, number][]>();
  for (const seg of segments) {
    const id = seg.objectId?.trim();
    if (!id || seg.path.length < 2) continue;
    byId.set(id, seg.path);
  }
  const uniqueIds = [...byId.keys()];
  if (uniqueIds.length === 0) return [];

  const anchorId =
    (anchorObjectId?.trim() && byId.has(anchorObjectId.trim()) ? anchorObjectId.trim() : null)
    ?? [...uniqueIds].sort()[0]!;
  const anchorPath = byId.get(anchorId);
  if (!anchorPath || anchorPath.length < 2) {
    const fallback: [number, number][] = [];
    for (const id of uniqueIds.sort()) {
      appendPath(fallback, byId.get(id)!, fallback.length > 0);
    }
    return fallback;
  }

  const remaining = new Set(uniqueIds);
  remaining.delete(anchorId);

  const points: [number, number][] = [...anchorPath];

  let endKey = endpointHash(points[points.length - 1]![0], points[points.length - 1]![1]);
  while (remaining.size > 0) {
    const chained = chainOneFromEnd(remaining, byId, endKey);
    if (!chained) break;
    remaining.delete(chained.id);
    appendPath(points, chained.path, true);
    endKey = endpointHash(points[points.length - 1]![0], points[points.length - 1]![1]);
  }

  let startKey = endpointHash(points[0]![0], points[0]![1]);
  while (remaining.size > 0) {
    const chained = chainOneFromStart(remaining, byId, startKey);
    if (!chained) break;
    remaining.delete(chained.id);
    prependPath(points, chained.path, true);
    startKey = endpointHash(points[0]![0], points[0]![1]);
  }

  return points;
}

export interface DiaryWorkSummaryPathSource {
  objectId?: string | null;
  path?: [number, number][] | null;
  lat?: number | null;
  lng?: number | null;
  endLat?: number | null;
  endLng?: number | null;
}

function toFiniteCoord(v: number | string | null | undefined): number | null {
  if (v == null || v === '') return null;
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? n : null;
}

/** Segment path `[lng, lat][]` from diary work-summary row (LineString or start/end fallback). */
export function diaryWorkSummarySegmentPath(entry: DiaryWorkSummaryPathSource): [number, number][] {
  const path = entry.path;
  if (Array.isArray(path) && path.length >= 2) {
    const out: [number, number][] = [];
    for (const coord of path) {
      if (!Array.isArray(coord) || coord.length < 2) continue;
      const lng = toFiniteCoord(coord[0]);
      const lat = toFiniteCoord(coord[1]);
      if (lng != null && lat != null) out.push([lng, lat]);
    }
    if (out.length >= 2) return out;
  }

  const lat = toFiniteCoord(entry.lat);
  const lng = toFiniteCoord(entry.lng);
  const endLat = toFiniteCoord(entry.endLat);
  const endLng = toFiniteCoord(entry.endLng);
  if (lat != null && lng != null && endLat != null && endLng != null) {
    return [[lng, lat], [endLng, endLat]];
  }
  return [];
}
